import {
  getUnifiedValue,
  resolveElementFromPath,
} from '@src/utility/compositionSettings';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { ImagePreset } from '../../ImagePreset';
import { ControlFieldLabel } from './ControlField';

import type {
  ControlRenderProps,
  HeadshotControlConfiguration,
} from '../types';
import type { ElementState } from '@src/renderer/ElementState';
import type { ElementLike } from '@src/utility/compositionSettings';

// HeadshotControl: renders a circular image selector for speaker headshots.
// When bound to a SpeakerControl, the headshot URL auto-populates from the selected speaker.
export const HeadshotControl = observer(
  ({
    control,
    activeElement,
    modifyProperty,
    compositionMeta,
  }: ControlRenderProps<HeadshotControlConfiguration>) => {
    const imageElement = resolveElementFromPath(
      activeElement as unknown as ElementLike,
      control.elementPaths[0] || [],
    );
    const { unifiedValue } = getUnifiedValue(
      control,
      activeElement,
      compositionMeta,
    );

    const currentUrl = typeof unifiedValue === 'string' ? unifiedValue : '';

    const onImageSourceChange = (newUrl: string) => {
      modifyProperty(control, newUrl);
    };

    if (!imageElement) return null;

    return (
      <Container>
        <ControlFieldLabel>{control.label}</ControlFieldLabel>
        <CircularPreviewWrapper>
          <ImagePreset
            activeElement={imageElement as unknown as ElementState}
            parentCompositionElement={activeElement}
            url={currentUrl || ''}
            onSourceChange={onImageSourceChange}
            isImageControl={true}
            isHeadshotControl={true}
          />
        </CircularPreviewWrapper>
      </Container>
    );
  },
);

export default HeadshotControl;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CircularPreviewWrapper = styled.div`
  img,
  video,
  > div > div:first-of-type {
    width: 80px;
    height: 80px;
    border-radius: 50%;
  }

  img,
  video {
    object-fit: cover;
  }

  > div > div:first-of-type {
    overflow: hidden;
  }
`;
